import { z } from 'zod';
import { AttachmentIdsSchema, PublicClientAttachmentSchema } from './client-attachments';

export const ApprovalStageSchema = z.enum(['topics', 'final_content']);
export type ApprovalStage = z.infer<typeof ApprovalStageSchema>;

export const ApprovalDecisionSchema = z.enum(['approved', 'changes_requested', 'rejected']);
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;

/**
 * Decision wording by batch phase (`stage`), never by the post status: the
 * label feeds the in-app notification title, the mirrored client-thread
 * comment prefix and the approval badge. It does NOT feed the summary e-mail,
 * which has its own stage wording.
 */
export function decisionLabel(decision: ApprovalDecision, stage: ApprovalStage): string {
  const subj = stage === 'topics' ? 'Téma' : 'Poszt';
  return decision === 'approved'
    ? `${subj} jóváhagyva`
    : decision === 'changes_requested'
      ? `${subj} módosítás kérve`
      : `${subj} elutasítva`;
}

export const CreateApprovalBatchSchema = z.object({
  monthlyPlanId: z.string().guid(),
  stage: ApprovalStageSchema,
  /** Primary recipient — recorded on the batch row + used in audit. */
  clientEmail: z.string().email().max(320),
  /** Additional recipients copied on the same email. Max 5 extras. */
  additionalEmails: z.array(z.string().email().max(320)).max(5).optional(),
  /**
   * Saved hotel contacts to copy on this batch, picked from the hotel's
   * notification-recipient list. Ids rather than addresses on purpose: the
   * server resolves them against the batch's own hotel, so a client cannot
   * name an address that was never saved for it.
   *
   * Independent of `additionalEmails` — the two are merged, not exchanged.
   */
  hotelContactIds: z.array(z.string().guid()).max(20).optional(),
  /** Optional manager-written intro shown in the email + on the public
   * view. Plain text, no HTML — newlines preserved. */
  note: z.string().max(2000).optional(),
  postIds: z.array(z.string().guid()).min(1).max(60),
});
export type CreateApprovalBatch = z.infer<typeof CreateApprovalBatchSchema>;

export const ApprovalBatchSchema = z.object({
  id: z.string().guid(),
  monthlyPlanId: z.string().guid(),
  hotelId: z.string().guid(),
  /** Present on org-wide listing; null on plan-scoped legacy responses. */
  hotelName: z.string().nullable().optional(),
  stage: ApprovalStageSchema,
  clientEmail: z.string(),
  /** Everyone copied on the approval e-mail besides `clientEmail`. */
  ccEmails: z.array(z.string()).default([]),
  note: z.string().nullable(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  viewCount: z.number().int().nonnegative(),
  lastViewedAt: z.string().nullable(),
  /** First client open (2026-09-23). */
  firstViewedAt: z.string().nullable(),
  reminderCount: z.number().int().nonnegative(),
  lastReminderAt: z.string().nullable(),
  createdBy: z.string().guid().nullable(),
  /** The colleague who sent it, Hungarian name order; null on legacy rows. */
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  postCount: z.number().int().nonnegative(),
  decisionCount: z.number().int().nonnegative(),
});
export type ApprovalBatch = z.infer<typeof ApprovalBatchSchema>;

/** Filters for the org-wide approval-batch listing (F7). */
export const ApprovalBatchQuerySchema = z.object({
  planId: z.string().guid().optional(),
  clientEmail: z.string().optional(),
  hotelId: z.string().guid().optional(),
  /** Computed lifecycle bucket. */
  status: z.enum(['pending', 'partial', 'completed', 'revoked', 'expired']).optional(),
  q: z.string().max(120).optional(),
  /**
   * Server-enforced page size. `.max()` is the point — the listing runs a
   * correlated aggregate per row, so an unbounded (or client-chosen) size lets
   * one request scale with the whole table. Defaults high enough that the
   * approvals page never notices.
   */
  limit: z.coerce.number().int().min(1).max(200).default(200),
});
export type ApprovalBatchQuery = z.infer<typeof ApprovalBatchQuerySchema>;

export const ApprovalBatchWithLinkSchema = ApprovalBatchSchema.extend({
  link: z.string().url(),
});
export type ApprovalBatchWithLink = z.infer<typeof ApprovalBatchWithLinkSchema>;

export const ApprovalDecisionRecordSchema = z.object({
  decision: ApprovalDecisionSchema,
  comment: z.string().nullable(),
  decidedAt: z.string(),
});
export type ApprovalDecisionRecord = z.infer<typeof ApprovalDecisionRecordSchema>;

export const PublicApprovalImageSchema = z.object({
  id: z.string().guid(),
  platform: z.enum(['fb', 'ig']),
  url: z.string().nullable(),
  caption: z.string().nullable(),
});
export type PublicApprovalImage = z.infer<typeof PublicApprovalImageSchema>;

export const PublicConversationEntrySchema = z.object({
  author: z.enum(['client', 'morgens']),
  /**
   * True when the entry was written by the address this batch was sent to
   * (the page may call it „Te”); false for other client addresses and for
   * Morgens. Optional: an older API does not send it.
   */
  self: z.boolean().optional(),
  body: z.string(),
  createdAt: z.string(),
  /** Images the client attached to this message; absent when none. */
  attachments: z.array(PublicClientAttachmentSchema).optional(),
});
export type PublicConversationEntry = z.infer<typeof PublicConversationEntrySchema>;

export const PublicApprovalPostSchema = z.object({
  id: z.string().guid(),
  scheduledFor: z.string(),
  brandOrSales: z.enum(['brand', 'sales']),
  category: z.string().nullable(),
  status: z.string(),
  topic: z
    .object({
      title: z.string(),
      angle: z.string().nullable(),
    })
    .nullable(),
  /** Hotel-library images attached to the post — shown in client view. */
  images: z.array(PublicApprovalImageSchema),
  contents: z.array(
    z.object({
      platform: z.enum(['fb', 'ig']),
      body: z.string(),
      hashtags: z.array(z.string()),
      sourceUrl: z.string().nullable(),
      /** Extra link the colleague attached to the text (pl. videó). */
      attachmentUrl: z.string().nullable(),
      /** Platform-specific decision, if the client decided on this one only. */
      decision: ApprovalDecisionRecordSchema.nullable(),
    }),
  ),
  /** Whole-post decision (NULL platform). Used by topics-stage batches. */
  decision: ApprovalDecisionRecordSchema.nullable(),
  /**
   * The client's decisions on this post from EARLIER batches (oldest first).
   * A second round shows what they asked to change, so they can verify the
   * fix landed without digging up the old email.
   */
  previousFeedback: z.array(
    ApprovalDecisionRecordSchema.extend({ platform: z.enum(['fb', 'ig']).nullable() }),
  ),
  /**
   * The post's `client` comment thread, oldest first — never the internal
   * one. Staff entries are `morgens` (no name, no user id); client entries
   * are `client` (no e-mail). Optional: an older API does not send it.
   */
  conversation: z.array(PublicConversationEntrySchema).optional(),
});
export type PublicApprovalPost = z.infer<typeof PublicApprovalPostSchema>;

export const PublicApprovalBatchSchema = z.object({
  stage: ApprovalStageSchema,
  hotelName: z.string(),
  monthLabel: z.string(),
  clientEmail: z.string(),
  note: z.string().nullable(),
  expiresAt: z.string(),
  revoked: z.boolean(),
  posts: z.array(PublicApprovalPostSchema),
  /** False (or absent on an older API) → the page offers no image upload. */
  attachmentsEnabled: z.boolean().optional(),
});
export type PublicApprovalBatch = z.infer<typeof PublicApprovalBatchSchema>;

export const HistoricalPostSchema = z.object({
  id: z.string().guid(),
  scheduledFor: z.string(),
  category: z.string().nullable(),
  brandOrSales: z.enum(['brand', 'sales']),
  topicTitle: z.string().nullable(),
  topicAngle: z.string().nullable(),
});
export type HistoricalPost = z.infer<typeof HistoricalPostSchema>;

export const ApprovalBatchPreviewSchema = PublicApprovalBatchSchema.extend({
  history: z.array(HistoricalPostSchema),
});
export type ApprovalBatchPreview = z.infer<typeof ApprovalBatchPreviewSchema>;

export const SubmitDecisionSchema = z.object({
  postId: z.string().guid(),
  decision: ApprovalDecisionSchema,
  comment: z.string().max(2000).optional(),
  /** Per-platform decision. Omit / null → whole post. */
  platform: z.enum(['fb', 'ig']).optional(),
  /** Only with `changes_requested` and a non-empty comment (server-checked). */
  attachmentIds: AttachmentIdsSchema,
});
export type SubmitDecision = z.infer<typeof SubmitDecisionSchema>;

/**
 * Bulk approve/reject across many posts in the same batch. Posts already
 * having a decision are silently skipped; the response reports counts.
 * Used by the public approval view's "Mind elfogad" / "Mind elutasít" buttons.
 */
export const SubmitBulkDecisionSchema = z.object({
  postIds: z.array(z.string().guid()).min(1).max(200),
  decision: ApprovalDecisionSchema,
  comment: z.string().max(2000).optional(),
});
export type SubmitBulkDecision = z.infer<typeof SubmitBulkDecisionSchema>;

export const BulkDecisionResultSchema = z.object({
  applied: z.number().int().min(0),
  skipped: z.number().int().min(0),
});
export type BulkDecisionResult = z.infer<typeof BulkDecisionResultSchema>;

/**
 * A client comment posted from the public approval view against one post in
 * the batch. Lands in that post's `client` comment thread (spec 8.2). The
 * author is the batch's recorded client email — no free-form author field.
 */
export const PublicApprovalCommentSchema = z.object({
  postId: z.string().guid(),
  body: z.string().min(1).max(2000),
  attachmentIds: AttachmentIdsSchema,
});
export type PublicApprovalComment = z.infer<typeof PublicApprovalCommentSchema>;
